import { useEffect } from 'react';
import { useAccessibility } from '../contexts/AccessibilityContext';
import { isAnyModalOpen } from './useModalDialog';

const isTypingTarget = (el) => {
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
};

/**
 * Global "T" shortcut that scrolls the page to the top and moves focus to
 * the main content landmark, mirroring the mouse-only "back to top" button
 * some pages show once you've scrolled down. AAC switch-scanning software
 * has no way to click a floating button, so this makes the same action
 * reachable from the keyboard on every page, not just where the button
 * happens to be rendered.
 */
export function useScrollToTopShortcut() {
  const { announce } = useAccessibility();

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (isAnyModalOpen()) return;
      if (isTypingTarget(document.activeElement)) return;
      if (event.key.toLowerCase() !== 't') return;

      event.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });

      const main = document.getElementById('main-content');
      if (main) {
        main.focus({ preventScroll: true });
      }

      announce('Scrolled to top of page');
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [announce]);
}
